import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Eye, Search, Download, X, ZoomIn, ZoomOut, RotateCcw, Filter, FileSpreadsheet, ChevronDown } from 'lucide-react';
import * as XLSX from 'xlsx';
import '../../Style/ReportView.css';
import ReportService from '../../Api/ReportService';
import { getDateString, ServiceProvider } from '../../Api/ServiceProvider';
import { toast } from 'react-toastify';
import permissionService from '../../Permission/Permission';
import { useHistoryList } from '../Breadcrum/HistoryProvider';
import Breadcrumbs from '../Breadcrum/BreadcrumbHistory';
import autoTable from 'jspdf-autotable';
import jsPDF from 'jspdf';
import SecureImage from '../ImgService/loadImage';
import logo from '../../Assets/logo.png';
import { uploadModalProps } from '../../Const/constant';

// ─────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────

const scannerDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'N/A';

    const day = date.getUTCDate().toString().padStart(2, '0');
    const month = (date.getUTCMonth() + 1).toString().padStart(2, '0');
    const year = date.getUTCFullYear();

    let hours = date.getUTCHours();
    const minutes = date.getUTCMinutes().toString().padStart(2, '0');

    const ampm = hours >= 12 ? 'pm' : 'am';
    hours = hours % 12 || 12;

    return `${day}/${month}/${year}, ${hours}:${minutes} ${ampm}`;
};

const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'N/A';
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    let hours = date.getHours();
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const ampm = hours >= 12 ? 'pm' : 'am';
    hours = hours % 12 || 12;
    return `${day}/${month}/${year}, ${hours}:${minutes} ${ampm}`;
};

// Returns today's date as YYYY-MM-DD for max attribute on date inputs
const getTodayString = () => new Date().toISOString().split('T')[0];

// Tab-specific status options
const PENDING_STATUSES = [
    { value: 'pending', label: 'Pending' },
    { value: 'dr_request', label: 'DR Request' },
    { value: 'in-progress', label: 'In Progress' },
];

const COMPLETED_STATUSES = [
    { value: 'completed', label: 'Completed' },
    { value: 'dr_approved', label: 'DR Approved' },
    { value: 'dr_completed', label: 'DR Completed' },
];

// ─────────────────────────────────────────────
// TYPE CONFIG
// ─────────────────────────────────────────────
const TYPE_CONFIG = {
    buildup: {
        name: 'buildup',
        label: `${uploadModalProps['buildup']}`,
        tableHeaders: [
            { key: 'sno', label: 'SNO' },
            { key: 'inspection_parameters', label: 'INSPECTION PARAMETERS' },
            { key: 'specification_with_unit_and_tolerance', label: 'SPECIFICATION WITH UNIT & TOLERANCE' },
            { key: 'observation', label: 'OBSERVATION' },
            { key: 'remarks', label: 'REMARKS' },
            { key: 'images', label: 'IMAGE', isImage: true, imageType: 'images' },
        ],
        hasImages: true,
        hasActions: false,
        statusField: 'buildup_status',
        getItemId: (item) => item.sno,
        searchFields: ['inspection_parameters', 'specification_with_unit_and_tolerance', 'measuring_instrument_or_method'],
        exportColumns: [{ wch: 10 }, { wch: 20 }, { wch: 30 }, { wch: 25 }, { wch: 25 }, { wch: 15 }, { wch: 20 }, { wch: 20 }],
    },
    test: {
        name: 'test',
        label: `${uploadModalProps['test']}`,
        tableHeaders: [
            { key: 'sno', label: 'SNO' },
            { key: 'inspection_parameters', label: 'INSPECTION PARAMETERS' },
            { key: 'specification_with_unit_and_tolerance', label: 'SPECIFICATION WITH UNIT & TOLERANCE' },
            { key: 'observation', label: 'OBSERVATION' },
            { key: 'remarks', label: 'REMARKS' },
            { key: 'images', label: 'IMAGE', isImage: true, imageType: 'images' },
        ],
        hasImages: true,
        hasActions: false,
        statusField: 'test_status',
        getItemId: (item) => item.sno,
        searchFields: ['inspection_parameters', 'specification_with_unit_and_tolerance', 'measuring_instrument_or_method'],
        exportColumns: [{ wch: 10 }, { wch: 20 }, { wch: 30 }, { wch: 25 }, { wch: 25 }, { wch: 15 }, { wch: 20 }, { wch: 20 }],
    },
    pdi: {
        name: 'pdi',
        label: `${uploadModalProps['pdi']}`,
        tableHeaders: [
            { key: 'sn', label: 'SNO' },
            { key: 'part_images', label: 'PART IMAGES', isImage: true, imageType: 'part_images' },
            { key: 'pn', label: 'PART NO' },
            { key: 'desc', label: 'DESCRIPTION' },
            { key: 'sz', label: 'SIZE' },
            { key: 'qty', label: 'QTY' },
            { key: 'p_qty', label: 'PACKED QTY' },
            { key: 'scannerDateTime', label: 'SCANNER DATE TIME' },
            { key: 'status', label: 'STATUS', isStatus: true },
            { key: 'verify', label: 'VERIFY' },
            { key: 'remarks', label: 'REMARKS' },
            { key: 'images', label: 'IMAGE', isImage: true, imageType: 'images' },
        ],
        hasImages: true,
        hasActions: false,
        statusField: 'pdi_status',
        getItemId: (item) => item.sn,
        searchFields: ['pn', 'desc', 'sz'],
        exportColumns: [{ wch: 10 }, { wch: 20 }, { wch: 30 }, { wch: 15 }, { wch: 10 }, { wch: 10 }, { wch: 20 }, { wch: 15 }, { wch: 20 }, { wch: 20 }],
    },
};

// ─────────────────────────────────────────────
// MULTI-SELECT STATUS DROPDOWN COMPONENT
// ─────────────────────────────────────────────
const StatusMultiSelect = ({ value, onChange, options }) => {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    // When the options list changes (tab switch) clear any selections that no longer exist
    useEffect(() => {
        const validValues = options.map(o => o.value);
        const stillValid = value.filter(v => validValues.includes(v));
        if (stillValid.length !== value.length) onChange(stillValid);
    }, [options]); // eslint-disable-line react-hooks/exhaustive-deps

    const toggle = (val) => {
        onChange(value.includes(val) ? value.filter(v => v !== val) : [...value, val]);
    };

    const triggerLabel = value.length === 0 || value.length === options.length
        ? 'All Statuses'
        : `${value.length} selected`;

    return (
        <div ref={ref} className="rv-multiselect" style={{ position: 'relative', minWidth: 160 }}>
            <button type="button" className="rv-multiselect-trigger" onClick={() => setOpen(o => !o)}>
                <Filter size={14} style={{ flexShrink: 0 }} />
                <span style={{ flex: 1, textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {triggerLabel}
                </span>
                <ChevronDown size={14} style={{ flexShrink: 0, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
            </button>

            {open && (
                <div className="rv-multiselect-dropdown">
                    <div className="rv-ms-all-row">
                        <button type="button" onClick={() => onChange(options.map(o => o.value))} className="rv-ms-link">All</button>
                        <span style={{ color: '#ccc' }}>|</span>
                        <button type="button" onClick={() => onChange([])} className="rv-ms-link">Clear</button>
                    </div>
                    {options.map(s => (
                        <label key={s.value} className="rv-ms-option">
                            <input
                                type="checkbox"
                                checked={value.includes(s.value)}
                                onChange={() => toggle(s.value)}
                            />
                            <span className={`report-status ${getStatusClassRaw(s.value)}`} style={{ fontSize: 11, padding: '2px 8px' }}>
                                {s.label}
                            </span>
                        </label>
                    ))}
                </div>
            )}
        </div>
    );
};

// Helper used inside StatusMultiSelect (no hook dependency)
function getStatusClassRaw(status) {
    const n = status?.toLowerCase();
    if (n === 'dr_request') return 'dr-request';
    if (n === 'dr_approved' || n === 'dr_completed') return 'dr-approved';
    if (n === 'completed') return 'complete';
    if (n === 'in-progress') return 'in-progress';
    if (n === 'pending') return 'pending';
    return 'inactive';
}

// ─────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────
const ReportView = () => {
    // ── Core state ──────────────────────────────
    const [reportsData, setReportsData] = useState({
        pending: { data: [], count: 0, lastFetch: null },
        completed: { data: [], count: 0, lastFetch: null },
    });
    const [selectedReport, setSelectedReport] = useState(null);
    const [viewType, setViewType] = useState(null);
    const [activeTab, setActiveTab] = useState('pending');
    const [searchQuery, setSearchQuery] = useState('');
    const [userData, setUserData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [exportingAll, setExportingAll] = useState(false);
    const [canView, setCanView] = useState(false);

    // ── Filter state — staged (editing in bar, not yet applied) ──
    const [stagedStartDate, setStagedStartDate] = useState('');
    const [stagedEndDate, setStagedEndDate] = useState('');
    const [stagedStatuses, setStagedStatuses] = useState([]); // [] = all

    // ── Filter state — applied (committed via Apply, drives the API) ──
    const [appliedStartDate, setAppliedStartDate] = useState('');
    const [appliedEndDate, setAppliedEndDate] = useState('');
    const [appliedStatuses, setAppliedStatuses] = useState([]); // [] = all

    // ── Pagination ──────────────────────────────
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(25);

    const [detailPagination, setDetailPagination] = useState({
        buildup: { searchTerm: '', currentPage: 1, itemsPerPage: 25 },
        test: { searchTerm: '', currentPage: 1, itemsPerPage: 25 },
        pdi: { searchTerm: '', currentPage: 1, itemsPerPage: 25 },
    });

    // ── Image viewer ────────────────────────────
    const [fullScreenViewer, setFullScreenViewer] = useState({
        isOpen: false, images: [], initialIndex: 0, viewType: 'pdi',
    });

    const viewTypeLabelMap = { test: 'Tested By', buildup: 'Assembled By', pdi: 'Inspected By' };
    const BASE_IMAGE_URL = ServiceProvider.assert.get;

    // ── Refs ─────────────────────────────────────
    const isInitializedRef = useRef(false);
    const isFetchingRef = useRef(false);
    const searchTimeoutRef = useRef(null);
    const abortControllerRef = useRef(null);
    const lastFetchParamsRef = useRef({ status: null, page: null, count: null, search: null, startDate: null, endDate: null, statuses: null, timestamp: null });

    const { addCustomPath, removeCustomPath } = useHistoryList();

    // ── Today string for date input max ─────────
    const todayStr = getTodayString();

    // ── Init ─────────────────────────────────────
    useEffect(() => {
        if (isInitializedRef.current) return;
        isInitializedRef.current = true;
        const user = JSON.parse(localStorage.getItem('user'));
        setUserData(user);
        if (user) {
            const hasView = permissionService.hasPermission('reports', 'view');
            setCanView(hasView);
            if (!hasView) {
                toast.error("You don't have permission to access Reports module", { autoClose: 5000, position: 'top-center' });
            }
        }
    }, []);



    // ── Core fetch (paginated) ───────────────────
    const fetchReports = useCallback(async (
        status, page, count, search = '',
        startDate = '', endDate = '', statuses = []
    ) => {
        if (isFetchingRef.current) return;
        if (!userData || !canView) return;

        const statusParam = statuses.length > 0 ? statuses.join(',') : '';
        const paramsKey = `${status}-${page}-${count}-${search}-${startDate}-${endDate}-${statusParam}`;
        const lastKey = `${lastFetchParamsRef.current.status}-${lastFetchParamsRef.current.page}-${lastFetchParamsRef.current.count}-${lastFetchParamsRef.current.search}-${lastFetchParamsRef.current.startDate}-${lastFetchParamsRef.current.endDate}-${lastFetchParamsRef.current.statuses}`;
        const now = Date.now();

        if (paramsKey === lastKey && now - (lastFetchParamsRef.current.timestamp || 0) < 1000) return;

        if (abortControllerRef.current) abortControllerRef.current.abort();
        abortControllerRef.current = new AbortController();

        isFetchingRef.current = true;
        setLoading(true);
        lastFetchParamsRef.current = { status, page, count, search, startDate, endDate, statuses: statusParam, timestamp: now };

        try {
            const response = await ReportService.getByStatus(
                status, page, count, search, startDate, endDate, statuses
            );

            if (response.success && response.data.length > 0) {
                setReportsData(prev => ({
                    ...prev,
                    [status]: { data: response.data || [], count: response.count || 0, lastFetch: new Date() },
                }));
            } else {
                setReportsData(prev => ({ ...prev, [status]: { data: [], count: 0, lastFetch: new Date() } }));
                toast.info(`No records found for ${status.charAt(0).toUpperCase() + status.slice(1)}`, { autoClose: 3000 });
            }
        } catch (err) {
            toast.error(String(err) || err?.error || 'Unable to fetch Reports', { autoClose: 3000 });
            setReportsData(prev => ({ ...prev, [status]: { data: [], count: 0, lastFetch: new Date() } }));
        } finally {
            setLoading(false);
            isFetchingRef.current = false;
        }
    }, [userData, canView]);

    // ── Fetch trigger effect ─────────────────────
    useEffect(() => {
        if (!canView || !userData) return;
        if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
        if (searchQuery.length > 0 && searchQuery.length < 3) return;

        const doFetch = () => fetchReports(
            activeTab, currentPage, itemsPerPage, searchQuery,
            appliedStartDate, appliedEndDate, appliedStatuses
        );

        if (searchQuery.length >= 3) {
            searchTimeoutRef.current = setTimeout(doFetch, 500);
        } else {
            doFetch();
        }

        return () => {
            if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
            if (abortControllerRef.current) abortControllerRef.current.abort();
        };
    }, [currentPage, itemsPerPage, activeTab, searchQuery, canView, userData, fetchReports,
        appliedStartDate, appliedEndDate, appliedStatuses]);

    // ── Apply filters ────────────────────────────
    const resetLastFetchRef = () => {
        lastFetchParamsRef.current = {
            status: null, page: null, count: null, search: null,
            startDate: null, endDate: null, statuses: null, timestamp: null,
        };
    };

    // FIXED: Only allow apply if dates are valid (both or neither)
    const handleApplyFilters = () => {
        // Check if only one date is selected (invalid state)
        const hasStart = !!stagedStartDate;
        const hasEnd = !!stagedEndDate;

        if ((hasStart && !hasEnd) || (!hasStart && hasEnd)) {
            toast.error('Please select both start and end dates', { autoClose: 3000 });
            return;
        }

        if (hasStart && hasEnd && stagedStartDate > stagedEndDate) {
            toast.error('Start date cannot be after end date', { autoClose: 3000 });
            return;
        }

        resetLastFetchRef();
        setAppliedStartDate(stagedStartDate);
        setAppliedEndDate(stagedEndDate);
        setAppliedStatuses(stagedStatuses);
        setCurrentPage(1);
    };

    // FIXED: Clear resets both staged and applied state so UI inputs clear immediately
    const handleClearFilters = () => {
        setStagedStartDate('');
        setStagedEndDate('');
        setStagedStatuses([]);
        resetLastFetchRef();
        setAppliedStartDate('');
        setAppliedEndDate('');
        setAppliedStatuses([]);
        setCurrentPage(1);
    };

    // FIXED: Check if staged filters are valid for enabling the Filter button
    // Valid if: (both dates selected) OR (no dates but statuses selected) OR (both dates and statuses)
    const hasValidStagedFilters = useMemo(() => {
        const hasStart = !!stagedStartDate;
        const hasEnd = !!stagedEndDate;
        const hasStatuses = stagedStatuses.length > 0;

        // Valid if: both dates present, or only statuses (no partial dates)
        if (hasStart && hasEnd) return true; // Both dates selected (statuses optional)
        if (!hasStart && !hasEnd && hasStatuses) return true; // Only statuses selected
        return false; // Invalid: only one date selected or nothing selected
    }, [stagedStartDate, stagedEndDate, stagedStatuses]);

    // Check both applied and staged filters for showing Clear button
    const hasActiveFilters = !!(appliedStartDate || appliedEndDate || appliedStatuses.length > 0);
    const hasStagedFilters = !!(stagedStartDate || stagedEndDate || stagedStatuses.length > 0);

    // Status options are tab-specific
    const activeTabStatusOptions = activeTab === 'pending' ? PENDING_STATUSES : COMPLETED_STATUSES;

    // ── Export ALL filtered data to Excel ────────
    const handleExportAllToExcel = async () => {
        if (!canView) { toast.error("No permission to export"); return; }
        setExportingAll(true);

        try {
            const response = await ReportService.getByStatus(
                activeTab, null, null, searchQuery,
                appliedStartDate, appliedEndDate, appliedStatuses
            );

            if (!response.success || !response.data?.length) {
                toast.warn('No data to export', { autoClose: 3000 });
                return;
            }

            const rows = response.data;

            // ── CHANGED: inserted *_REMARK columns after each *_STATUS column ──
            const headers = [
                'NO.',
                'TPL NO',
                'DATE CREATED',
                'PO ID',
                'FAB NO',
                'MODIFIED DATE',
                `${uploadModalProps['buildup']} STATUS`,
                `${uploadModalProps['buildup']} REMARK`,   // NEW
                `${uploadModalProps['test']} STATUS`,
                `${uploadModalProps['test']} REMARK`,       // NEW
                `${uploadModalProps['pdi']} STATUS`,
                `${uploadModalProps['pdi']} REMARK`,        // NEW
                'APPROVED REMARK',
                'COMPLETED REMARK',
            ];

            // ── CHANGED: added r.buildup_remark, r.test_remark, r.pdi_remark ──
            const wsData = [headers, ...rows.map((r, idx) => [
                idx + 1,
                r.tpl_no || '',
                r.cd ? formatDateTime(r.cd) : 'N/A',
                r.po_no || '',
                r.fab_no || '',
                r.md ? formatDateTime(r.md) : 'N/A',
                getStatusDisplay(r.buildup_status),
                r.buildup_remark || '',
                getStatusDisplay(r.test_status),
                r.test_remark || '',
                getStatusDisplay(r.pdi_status),
                r.pdi_remark || '',
                r.apr_remark || '',
                r.com_remark || '',
            ])];

            const wb = XLSX.utils.book_new();
            const ws = XLSX.utils.aoa_to_sheet(wsData);

            // ── CHANGED: widths now match the 14-column layout ──
            ws['!cols'] = [
                { wch: 6 },   // NO.
                { wch: 14 },  // TPL NO
                { wch: 20 },  // DATE CREATED
                { wch: 14 },  // PO ID
                { wch: 14 },  // FAB NO
                { wch: 20 },  // MODIFIED DATE
                { wch: 18 },  // BUILDUP STATUS
                { wch: 25 },  // BUILDUP REMARK
                { wch: 18 },  // TEST STATUS
                { wch: 25 },  // TEST REMARK
                { wch: 18 },  // PDI STATUS
                { wch: 25 },  // PDI REMARK
                { wch: 25 },  // APPROVED REMARK
                { wch: 25 },  // COMPLETED REMARK
            ];

            XLSX.utils.book_append_sheet(wb, ws, 'Reports');

            const tab = activeTab.charAt(0).toUpperCase() + activeTab.slice(1);
            const today = getDateString(new Date());
            const filterSuffix = [
                appliedStartDate ? `From_${appliedStartDate}` : '',
                appliedEndDate ? `To_${appliedEndDate}` : '',
            ].filter(Boolean).join('_');

            const fileName = `Reports_${tab}${filterSuffix ? '_' + filterSuffix : ''}_${today}.xlsx`;
            XLSX.writeFile(wb, fileName);

            toast.success(`Exported ${rows.length} records successfully`, { autoClose: 3000 });

        } catch (err) {
            console.error('Export error:', err);
            toast.error('Failed to export data', { autoClose: 3000 });
        } finally {
            setExportingAll(false);
        }
    };

    // ── Memos ────────────────────────────────────
    const currentReports = useMemo(() => reportsData[activeTab]?.data || [], [reportsData, activeTab]);
    const currentCount = useMemo(() => reportsData[activeTab]?.count || 0, [reportsData, activeTab]);
    const mainTotalPages = useMemo(() => Math.ceil(currentCount / itemsPerPage) || 1, [currentCount, itemsPerPage]);

    // ── Detail view helpers ──────────────────────
    const handleViewSection = (report, type) => {
        if (!canView) { toast.error("You don't have permission to view reports"); return; }
        setSelectedReport(report);
        setViewType(type);
        setDetailPagination(prev => ({ ...prev, [type]: { searchTerm: '', currentPage: 1, itemsPerPage: 25 } }));
    };
    const handleBackToList = () => { setSelectedReport(null); setViewType(null); };
    const backNav = () => { handleBackToList(); removeCustomPath(); };

    const handleViewImages = (item, imageType = 'images') => {
        const images = item[imageType] || [];
        if (images.length > 0) setFullScreenViewer({ isOpen: true, images, initialIndex: 0, viewType });
    };
    const handleCloseFullScreenViewer = () => setFullScreenViewer({ isOpen: false, images: [], initialIndex: 0, viewType: 'pdi' });

    const updateDetailPagination = (type, updates) =>
        setDetailPagination(prev => ({ ...prev, [type]: { ...prev[type], ...updates } }));

    const getDetailItems = () => selectedReport?.[viewType] || [];
    const getFilteredDetailList = () => {
        const items = getDetailItems();
        const current = detailPagination[viewType];
        const typeConfig = TYPE_CONFIG[viewType];
        if (!current?.searchTerm) return items;
        const term = current.searchTerm.toLowerCase();
        return items.filter(item => typeConfig.searchFields.some(f => item[f]?.toLowerCase().includes(term)));
    };
    const getPaginatedDetailList = () => {
        const filtered = getFilteredDetailList();
        const { currentPage: cp, itemsPerPage: ip } = detailPagination[viewType];
        return filtered.slice((cp - 1) * ip, cp * ip);
    };
    const getDetailTotalPages = () => Math.ceil(getFilteredDetailList().length / detailPagination[viewType].itemsPerPage) || 1;

    // ── Status helpers ───────────────────────────
    const getStatusDisplay = (status) => {
        if (!status) return 'NA';
        const n = status.toLowerCase();
        if (n === 'completed') return 'Completed';
        if (n === 'in-progress') return 'In Progress';
        if (n === 'pending') return 'Pending';
        if (n === 'dr_request') return 'Dr Request';
        if (n === 'dr_approved') return 'Dr Approved';
        if (n === 'dr_completed') return 'Dr Completed';
        return 'NA';
    };
    const getStatusClass = (status) => `report-status ${getStatusClassRaw(status)}`;

    // ── Image helpers ────────────────────────────
    const getImageUrl = (item, imageType = 'images', vt = 'pdi') => {
        const images = item[imageType] || [];
        if (!images.length) return null;
        return `${BASE_IMAGE_URL}images/${vt}_report/${images[0]}`;
    };

    const loadReportImages = async (items, imageKey, reportType) => {
        if (!items || !imageKey || !reportType || !userData?.accessToken) return Array(items.length).fill(null);
        return Promise.all(items.map(async (item) => {
            if (item[imageKey]?.length > 0) {
                try {
                    const res = await fetch(`${BASE_IMAGE_URL}images/${reportType}_report/${item[imageKey][0]}`, {
                        headers: { 'Authorization': `Bearer ${userData.accessToken}` },
                    });
                    if (!res.ok) throw new Error();
                    const blob = await res.blob();
                    return new Promise((resolve, reject) => {
                        const reader = new FileReader();
                        reader.onload = () => resolve(reader.result);
                        reader.onerror = reject;
                        reader.readAsDataURL(blob);
                    });
                } catch { return null; }
            }
            return null;
        }));
    };

    // ── PDF export (detail view) ─────────────────
    const exportToExcel = async (data, type) => {
        const doc = new jsPDF('l', 'mm', 'a4');
        const pageWidth = doc.internal.pageSize.getWidth();
        const margin = 10;
        const totalWidth = pageWidth - margin * 2;
        let yPosition = 15;

        doc.setLineWidth(0.2);
        doc.rect(margin, 10, totalWidth, 20);
        doc.addImage(logo, 'PNG', margin + 2, 12, 35, 16);
        doc.setFontSize(16);
        doc.setFont('helvetica', 'bold');
        doc.text(`${uploadModalProps[type].toUpperCase()} REPORT`, pageWidth / 2, 22, { align: 'center' });
        yPosition = 30;

        const halfWidth = totalWidth / 2;
        const labelWidth = 30;
        const valueWidth = halfWidth - labelWidth;

        autoTable(doc, {
            startY: yPosition,
            head: [],
            body: [
                [{ content: 'TPL NO:', styles: { fontStyle: 'bold' } }, data?.tpl_no || '', { content: 'Fab no:', styles: { fontStyle: 'bold' } }, data?.fab_no || ''],
                [{ content: 'TPL DESC:', styles: { fontStyle: 'bold' } }, data?.[`${type}_desc`] || '', { content: 'Pro.order no:', styles: { fontStyle: 'bold' } }, data?.po_no || ''],
                [{ content: 'Operator:', styles: { fontStyle: 'bold' } }, { content: `${data[`${type}_ckr_dts`] || 'N/A'}`, _addSignature: true }, { content: 'Date:', styles: { fontStyle: 'bold' } }, `${getDateString(new Date(data[`${type}_md`]))}`],
            ],
            theme: 'grid',
            styles: { fontSize: 10, cellPadding: 3, lineColor: [0, 0, 0], lineWidth: 0.2, valign: 'middle' },
            columnStyles: {
                0: { cellWidth: labelWidth, halign: 'right' }, 1: { cellWidth: valueWidth },
                2: { cellWidth: labelWidth, halign: 'right' }, 3: { cellWidth: valueWidth },
            },
            margin: { left: margin, right: margin },
            didDrawCell: (dataCell) => {
                if (dataCell.row.index === 2 && dataCell.column.index === 1) {
                    const sigData = data[`${type}_ckr_sign`];
                    if (sigData) {
                        const { x, y, height } = dataCell.cell;
                        const tw = doc.getTextWidth(dataCell.cell.text[0]);
                        try { doc.addImage(sigData, 'PNG', x + tw + 10, y + (height - 8) / 2, 16, 8); } catch { }
                    }
                }
            },
        });

        yPosition = doc.lastAutoTable.finalY;

        if (type === 'test' || type === 'buildup') {
            const reportData = data?.[type] || [];
            const imageDataArray = await loadReportImages(reportData, 'images', type);
            const col1 = 20, col2 = 35, col3 = 75, col4 = 65, col5 = 50;
            const col6 = totalWidth - (col1 + col2 + col3 + col4 + col5);

            autoTable(doc, {
                startY: yPosition,
                head: [['S.no', 'Image', 'Inspection characteristics', 'Specification', 'Observation', 'Remarks']],
                body: [
                    ...reportData.map(item => [item.sno, '-', item.inspection_parameters || '', item.specification_with_unit_and_tolerance || '', item.observation || '', item.remarks || '']),
                    [{ content: 'Remarks:', styles: { fontStyle: 'bold' }, colSpan: 2 }, { content: data[`${type}_remark`] || '', colSpan: 4 }],
                    [{ content: 'Document No:', styles: { fontStyle: 'bold' }, colSpan: 2 }, { content: data[`${type}_document_no`] || '', colSpan: 4 }],
                ],
                theme: 'grid', showHead: 'firstPage',
                styles: { fontSize: 9, cellPadding: 3, lineColor: [0, 0, 0], lineWidth: 0.2, valign: 'middle' },
                headStyles: { fillColor: [211, 211, 211], textColor: [0, 0, 0], fontStyle: 'bold', halign: 'center', fontSize: 10 },
                columnStyles: { 0: { cellWidth: col1, halign: 'center' }, 1: { cellWidth: col2, halign: 'center' }, 2: { cellWidth: col3 }, 3: { cellWidth: col4 }, 4: { cellWidth: col5 }, 5: { cellWidth: col6, halign: 'center' } },
                margin: { left: margin, right: margin }, tableWidth: totalWidth,
                didParseCell: (d) => { if (d.section === 'body' && d.column.index === 1 && d.row.raw[1] === '-' && imageDataArray[d.row.index]) d.cell.styles.minCellHeight = 30; },
                didDrawCell: (d) => {
                    if (d.section === 'body' && d.column.index === 1 && d.row.raw[1] === '-') {
                        const img = imageDataArray[d.row.index];
                        if (img) { const p = 3; try { doc.addImage(img, 'JPEG', d.cell.x + p, d.cell.y + p, d.cell.width - 2 * p, d.cell.height - 2 * p); } catch { } }
                    }
                },
            });
        } else if (type === 'pdi') {
            const pdiData = data?.pdi || [];
            const partImgArr = await loadReportImages(pdiData, 'part_images', 'pdi');
            const refImgArr = await loadReportImages(pdiData, 'images', 'pdi');
            const col1 = 14.5, col2 = 32, col3 = 32, col4 = 35, col5 = 25, col6 = 70, col7 = 15, col8 = 15, col9 = 15;
            const col10 = totalWidth - (col1 + col2 + col3 + col4 + col5 + col6 + col7 + col8 + col9);

            autoTable(doc, {
                startY: yPosition,
                head: [['Sl.No', 'Part Image', 'Ref. Image', 'Part No.', 'Dimensions', 'Part Description', 'Qty/unit', 'Box Count', 'Verify', 'Remarks']],
                body: [
                    ...pdiData.map(item => [String(item.sn || ''), '-', '-', item.pn || '', item.sz || '', item.desc || '', String(item.qty || ''), String(item.p_qty || ''), String(item.verify || 'N/A'), item.remarks || '']),
                    [{ content: 'Remarks:', styles: { fontStyle: 'bold' }, colSpan: 3 }, { content: data[`${type}_remark`] || '', colSpan: 6 }],
                    [{ content: 'Document No:', styles: { fontStyle: 'bold' }, colSpan: 3 }, { content: data[`${type}_document_no`] || '', colSpan: 6 }],
                ],
                theme: 'grid', showHead: 'firstPage',
                styles: { fontSize: 8, cellPadding: 3, lineColor: [0, 0, 0], lineWidth: 0.2, valign: 'middle' },
                headStyles: { fillColor: [211, 211, 211], textColor: [0, 0, 0], fontStyle: 'bold', halign: 'center', fontSize: 9 },
                columnStyles: { 0: { cellWidth: col1, halign: 'center' }, 1: { cellWidth: col2, halign: 'center' }, 2: { cellWidth: col3, halign: 'center' }, 3: { cellWidth: col4 }, 4: { cellWidth: col5, halign: 'center' }, 5: { cellWidth: col6 }, 6: { cellWidth: col7, halign: 'center' }, 7: { cellWidth: col8, halign: 'center' }, 8: { cellWidth: col9, halign: 'center' }, 9: { cellWidth: col10 } },
                margin: { left: margin, right: margin }, tableWidth: totalWidth,
                didParseCell: (d) => {
                    const ri = d.row.index;
                    if (d.section === 'body' && d.column.index === 1 && d.row.raw[1] === '-' && partImgArr[ri]) d.cell.styles.minCellHeight = 28;
                    if (d.section === 'body' && d.column.index === 2 && d.row.raw[2] === '-' && refImgArr[ri]) d.cell.styles.minCellHeight = 28;
                },
                didDrawCell: (d) => {
                    const ri = d.row.index; const { x, y, width, height } = d.cell; const p = 3;
                    if (d.section === 'body' && d.column.index === 1 && d.row.raw[1] === '-' && partImgArr[ri]) try { doc.addImage(partImgArr[ri], 'JPEG', x + p, y + p, width - 2 * p, height - 2 * p); } catch { }
                    if (d.section === 'body' && d.column.index === 2 && d.row.raw[2] === '-' && refImgArr[ri]) try { doc.addImage(refImgArr[ri], 'JPEG', x + p, y + p, width - 2 * p, height - 2 * p); } catch { }
                },
            });
        }

        doc.save(`${uploadModalProps[type].toUpperCase()} Report-${data?.tpl_no}-${getDateString()}.pdf`);
    };

    // ── Pagination handlers ──────────────────────
    const handleFirstPage = () => { if (currentPage !== 1) { resetLastFetchRef(); setCurrentPage(1); } };
    const handlePreviousPage = () => setCurrentPage(p => Math.max(1, p - 1));
    const handleNextPage = () => setCurrentPage(p => Math.min(mainTotalPages, p + 1));
    const handleLastPage = () => { if (currentPage !== mainTotalPages) setCurrentPage(mainTotalPages); };
    const handlePageSizeChange = (v) => { resetLastFetchRef(); setItemsPerPage(v); setCurrentPage(1); };
    const handleTabChange = (tab) => {
        if (tab !== activeTab) {
            // Also reset staged filters on tab change so UI is consistent
            setStagedStartDate('');
            setStagedEndDate('');
            setStagedStatuses([]);
            setAppliedEndDate('');
            setAppliedStartDate('');
            setAppliedStatuses([]);
            resetLastFetchRef();
            setActiveTab(tab);
            setCurrentPage(1);
        }
    };

    // ═══════════════════════════════════════════
    // FULL-SCREEN IMAGE VIEWER
    // ═══════════════════════════════════════════
    const FullScreenImageViewer = ({ isOpen, onClose, images, initialIndex, viewType: vt }) => {
        const [currentIndex, setCurrentIndex] = useState(initialIndex);
        const [scale, setScale] = useState(1);
        const [position, setPosition] = useState({ x: 0, y: 0 });
        const [isDragging, setIsDragging] = useState(false);
        const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
        const lastTouchDistRef = useRef(null);
        const MIN_SCALE = 1, MAX_SCALE = 5, ZOOM_STEP = 0.5;

        useEffect(() => { setCurrentIndex(initialIndex); resetZoom(); }, [initialIndex, isOpen]);
        useEffect(() => { resetZoom(); }, [currentIndex]);

        const resetZoom = () => { setScale(1); setPosition({ x: 0, y: 0 }); };
        const zoomIn = (e) => { e?.stopPropagation(); setScale(p => Math.min(p + ZOOM_STEP, MAX_SCALE)); };
        const zoomOut = (e) => { e?.stopPropagation(); setScale(p => { const n = p - ZOOM_STEP; if (n <= MIN_SCALE) { setPosition({ x: 0, y: 0 }); return MIN_SCALE; } return n; }); };

        const handleWheel = (e) => { e.preventDefault(); const d = e.deltaY < 0 ? 0.15 : -0.15; setScale(p => { const n = p + d; if (n <= MIN_SCALE) { setPosition({ x: 0, y: 0 }); return MIN_SCALE; } return Math.min(n, MAX_SCALE); }); };
        const handleMouseDown = (e) => { if (scale > 1) { e.preventDefault(); setIsDragging(true); setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y }); } };
        const handleMouseMove = (e) => { if (isDragging && scale > 1) setPosition({ x: e.clientX - dragStart.x, y: e.clientY - dragStart.y }); };
        const handleMouseUp = () => setIsDragging(false);

        const handleTouchStart = (e) => {
            if (e.touches.length === 2) { const dx = e.touches[0].clientX - e.touches[1].clientX, dy = e.touches[0].clientY - e.touches[1].clientY; lastTouchDistRef.current = Math.sqrt(dx * dx + dy * dy); }
            else if (e.touches.length === 1 && scale > 1) { setIsDragging(true); setDragStart({ x: e.touches[0].clientX - position.x, y: e.touches[0].clientY - position.y }); }
        };
        const handleTouchMove = (e) => {
            e.preventDefault();
            if (e.touches.length === 2) {
                const dx = e.touches[0].clientX - e.touches[1].clientX, dy = e.touches[0].clientY - e.touches[1].clientY;
                const dist = Math.sqrt(dx * dx + dy * dy);
                if (lastTouchDistRef.current !== null) { const delta = dist - lastTouchDistRef.current; setScale(p => { const n = p + delta * 0.01; if (n <= MIN_SCALE) { setPosition({ x: 0, y: 0 }); return MIN_SCALE; } return Math.min(n, MAX_SCALE); }); }
                lastTouchDistRef.current = dist;
            } else if (e.touches.length === 1 && isDragging && scale > 1) { setPosition({ x: e.touches[0].clientX - dragStart.x, y: e.touches[0].clientY - dragStart.y }); }
        };
        const handleTouchEnd = () => { setIsDragging(false); lastTouchDistRef.current = null; };
        const handlePrev = (e) => { e.stopPropagation(); setCurrentIndex(p => (p - 1 + images.length) % images.length); };
        const handleNext = (e) => { e.stopPropagation(); setCurrentIndex(p => (p + 1) % images.length); };

        if (!isOpen || !images.length) return null;

        return (
            <div className="fullscreen-image-viewer" onClick={onClose}
                style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.86)', zIndex: 9999, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                {/* Toolbar */}
                <div onClick={e => e.stopPropagation()}
                    style={{ position: 'absolute', top: 0, left: 0, right: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 16px', background: 'linear-gradient(to bottom,rgba(0,0,0,0.7),transparent)', zIndex: 10 }}>
                    <span style={{ color: '#fff', fontSize: 14, fontWeight: 500 }}>{currentIndex + 1} / {images.length}</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        {[
                            { action: zoomOut, icon: <ZoomOut size={16} />, disabled: scale <= MIN_SCALE, title: 'Zoom Out' },
                            { action: zoomIn, icon: <ZoomIn size={16} />, disabled: scale >= MAX_SCALE, title: 'Zoom In' },
                            { action: (e) => { e.stopPropagation(); resetZoom(); }, icon: <RotateCcw size={15} />, disabled: scale === 1, title: 'Reset' },
                        ].map((btn, i) => (
                            <button key={i} onClick={btn.action} disabled={btn.disabled} title={btn.title}
                                style={{ background: btn.disabled ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.2)', border: 'none', borderRadius: 6, color: btn.disabled ? 'rgba(255,255,255,0.3)' : '#fff', padding: '6px 10px', cursor: btn.disabled ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center' }}>
                                {btn.icon}
                            </button>
                        ))}
                        <span style={{ color: '#fff', fontSize: 13, background: 'rgba(255,255,255,0.15)', borderRadius: 6, padding: '4px 8px', minWidth: 44, textAlign: 'center' }}>{Math.round(scale * 100)}%</span>
                    </div>
                    <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: 6, color: '#fff', padding: '6px 10px', cursor: 'pointer', display: 'flex', alignItems: 'center' }}><X size={20} /></button>
                </div>

                {/* Image */}
                <div onClick={e => e.stopPropagation()} onWheel={handleWheel} onMouseDown={handleMouseDown} onMouseMove={handleMouseMove} onMouseUp={handleMouseUp} onMouseLeave={handleMouseUp}
                    onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onTouchEnd={handleTouchEnd}
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%', overflow: 'hidden', cursor: scale > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default', userSelect: 'none', padding: '60px 80px', boxSizing: 'border-box' }}>
                    <div style={{ transform: `scale(${scale}) translate(${position.x / scale}px,${position.y / scale}px)`, transformOrigin: 'center center', transition: isDragging ? 'none' : 'transform 0.15s ease', display: 'flex', alignItems: 'center', justifyContent: 'center', maxWidth: '30%', maxHeight: '100%' }}>
                        <SecureImage url={`${ServiceProvider.assert.get}images/${vt}_report/${images[currentIndex]}`} token={userData?.accessToken} alt={`Image ${currentIndex + 1}`}
                            style={{ maxWidth: '100%', maxHeight: 'calc(100vh - 140px)', objectFit: 'contain', borderRadius: 4, display: 'block', pointerEvents: 'none' }} />
                    </div>
                </div>

                {/* Nav arrows */}
                {images.length > 1 && ['left', 'right'].map((side, i) => (
                    <button key={side} onClick={i === 0 ? handlePrev : handleNext}
                        style={{ position: 'absolute', [side]: '12px', top: '50%', transform: 'translateY(-50%)', background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: '50%', color: '#fff', width: 44, height: 44, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, zIndex: 10, backdropFilter: 'blur(4px)' }}
                        onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.3)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.15)'}>
                        {i === 0 ? '❮' : '❯'}
                    </button>
                ))}

                {/* Thumbnails */}
                {images.length > 1 && (
                    <div onClick={e => e.stopPropagation()} style={{ position: 'absolute', bottom: 0, left: 0, right: 0, display: 'flex', justifyContent: 'center', gap: 6, padding: '10px 16px', background: 'linear-gradient(to top,rgba(0,0,0,0.7),transparent)', overflowX: 'auto' }}>
                        {images.map((img, idx) => (
                            <div key={idx} onClick={() => setCurrentIndex(idx)}
                                style={{ width: 40, height: 40, borderRadius: 4, border: idx === currentIndex ? '2px solid #4fc3f7' : '2px solid transparent', opacity: idx === currentIndex ? 1 : 0.5, cursor: 'pointer', flexShrink: 0, overflow: 'hidden', transition: 'all 0.2s' }}>
                                <SecureImage url={`${ServiceProvider.assert.get}images/${vt}_report/${img}`} token={userData?.accessToken} alt={`Thumb ${idx + 1}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            </div>
                        ))}
                    </div>
                )}
                <div onClick={e => e.stopPropagation()} style={{ position: 'absolute', bottom: images.length > 1 ? 68 : 12, left: '50%', transform: 'translateX(-50%)', color: 'rgba(255,255,255,0.4)', fontSize: 11, pointerEvents: 'none', whiteSpace: 'nowrap' }}>
                    Scroll to zoom · Drag to pan when zoomed
                </div>
            </div>
        );
    };

    // ═══════════════════════════════════════════
    // DETAIL VIEW
    // ═══════════════════════════════════════════
    const renderDetailView = () => {
        if (!selectedReport || !viewType) return null;
        const typeConfig = TYPE_CONFIG[viewType];
        const current = detailPagination[viewType];
        const filtered = getFilteredDetailList();
        const paginated = getPaginatedDetailList();
        const totalPages = getDetailTotalPages();

        return (
            <div className="report-detail-view-container">
                <div className="report-sticky-header">
                    <div style={{ padding: '10px' }} className="report-approver-section">
                        <div className="report-detail-info">
                            <span><strong>PO NO:</strong> {selectedReport.po_no}</span>
                            <span><strong>TPL NO:</strong> {selectedReport.tpl_no}</span>
                            <span><strong>FAB NO:</strong> {selectedReport.fab_no}</span>
                            <span><strong>{viewTypeLabelMap[viewType]}:</strong> {selectedReport[`${viewType}_ckr_dts`]}</span>
                            <span><strong>Date Created:</strong> {formatDateTime(selectedReport.cd)}</span>
                        </div>
                    </div>
                    <div style={{ padding: '10px' }} className="report-approver-section">
                        <div className="report-detail-info">
                            <span><strong>Remark:</strong> {selectedReport[`${viewType}_remark`]}</span>
                        </div>
                    </div>
                    <div className="report-approver-section" style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
                        <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', alignItems: 'center' }}>
                            <span><span style={{ fontWeight: 'bold' }}>{uploadModalProps[viewType].toUpperCase()} DESC:</span> {selectedReport[`${viewType}_desc`]}</span>
                            <span><span style={{ fontWeight: 'bold' }}>DOC NO:</span> {selectedReport[`${viewType}_document_no`]}</span>
                        </div>
                        <div className="report-controls">
                            <div className="report-search-box">
                                <Search size={16} />
                                <input type="text" placeholder="Search..." value={current.searchTerm}
                                    onChange={e => updateDetailPagination(viewType, { searchTerm: e.target.value, currentPage: 1 })} />
                            </div>
                            <button className="report-export-btn" onClick={() => exportToExcel(selectedReport, viewType)}>
                                <Download size={16} /> Export PDF
                            </button>
                        </div>
                    </div>
                </div>

                <div className="report-scrollable-container">
                    <div className="report-table-container">
                        <div className="report-sub-table-wrapper">
                            <table className="report-table">
                                <thead>
                                    <tr>{typeConfig.tableHeaders.map((h, i) => <th key={i}>{h.label}</th>)}</tr>
                                </thead>
                                <tbody>
                                    {paginated.map(item => (
                                        <tr key={typeConfig.getItemId(item)}>
                                            {typeConfig.tableHeaders.map((header, hi) => {
                                                if (header.isImage) {
                                                    const imageUrl = getImageUrl(item, header.imageType, viewType);
                                                    return item[header.imageType]?.length > 0 ? (
                                                        <td key={hi} className="report-image-cell" onClick={() => handleViewImages(item, header.imageType)} style={{ cursor: 'pointer' }}>
                                                            <div className="report-avatar" title={`Click to view ${item[header.imageType].length} image(s)`}>
                                                                {imageUrl ? <SecureImage url={imageUrl} token={userData?.accessToken} alt="Item" className="report-item-image" /> : <div className="report-image-placeholder" />}
                                                            </div>
                                                        </td>
                                                    ) : <td key={hi}></td>;
                                                }
                                                if (header.isStatus) return <td key={hi}><span className={getStatusClass(item.status)}>{getStatusDisplay(item.status)}</span></td>;
                                                if (header.key === 'scannerDateTime') return <td key={hi}>{scannerDate(item[header.key])}</td>;
                                                if (header.key === 'verify') return <td key={hi}>{item.verify_m ? (item[header.key] ?? '0') : 'N/A'}</td>;
                                                return <td key={hi}>{item[header.key]}</td>;
                                            })}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                {filtered.length > 0 && (
                    <div className="report-pagination">
                        <div className="report-rows-per-page">
                            <span>Rows per page:</span>
                            <select value={current.itemsPerPage} onChange={e => updateDetailPagination(viewType, { itemsPerPage: +e.target.value, currentPage: 1 })}>
                                {[10, 15, 25, 50, 100].map(v => <option key={v} value={v}>{v}</option>)}
                            </select>
                        </div>
                        <span>{Math.min((current.currentPage - 1) * current.itemsPerPage + 1, filtered.length)}–{Math.min(current.currentPage * current.itemsPerPage, filtered.length)} of {filtered.length}</span>
                        <div className="report-pagination-controls">
                            <button onClick={() => updateDetailPagination(viewType, { currentPage: 1 })} disabled={current.currentPage === 1}>⟪</button>
                            <button onClick={() => updateDetailPagination(viewType, { currentPage: current.currentPage - 1 })} disabled={current.currentPage === 1}>⟨</button>
                            <button onClick={() => updateDetailPagination(viewType, { currentPage: current.currentPage + 1 })} disabled={current.currentPage >= totalPages}>⟩</button>
                            <button onClick={() => updateDetailPagination(viewType, { currentPage: totalPages })} disabled={current.currentPage >= totalPages}>⟫</button>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    // ═══════════════════════════════════════════
    // MAIN RENDER
    // ═══════════════════════════════════════════
    return (
        <div className="report-container">
            <Breadcrumbs backNav={backNav} />
            {selectedReport ? renderDetailView() : (
                <>
                    {/* ── STICKY HEADER ─────────────────────────── */}
                    <div className="report-sticky-header">

                        {/* ROW 1 : Tabs + Search */}
                        <div className="report-header-content">
                            <div className="report-tabs">
                                {['pending', 'completed'].map(tab => (
                                    <button key={tab}
                                        className={`report-tab-btn ${activeTab === tab ? 'active' : ''}`}
                                        onClick={() => handleTabChange(tab)}>
                                        {tab.toUpperCase()}
                                    </button>
                                ))}
                            </div>
                            <div className="report-controls">
                                <div className="report-search-box">
                                    <Search size={16} />
                                    <input type="text" placeholder="Search... (min 3 chars)" value={searchQuery}
                                        onChange={e => { setSearchQuery(e.target.value); setCurrentPage(1); }} />
                                </div>
                                {searchQuery && (
                                    <button className="rv-clear-btn" onClick={() => { setSearchQuery(''); setCurrentPage(1); }} title="Clear search">
                                        <X size={15} />
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* ROW 2 : Filter bar */}
                        <div className="rv-filter-bar">
                            {/* Start Date — max = today, future dates blocked */}
                            <div className="rv-filter-group">
                                <label className="rv-filter-label">Start Date</label>
                                <input
                                    type="date"
                                    className="rv-date-input"
                                    value={stagedStartDate}
                                    max={stagedEndDate || todayStr}
                                    onChange={e => setStagedStartDate(e.target.value)}
                                />
                            </div>

                            <div className="rv-filter-sep">→</div>

                            {/* End Date — max = today, min = start date */}
                            <div className="rv-filter-group">
                                <label className="rv-filter-label">End Date</label>
                                <input
                                    type="date"
                                    className="rv-date-input"
                                    value={stagedEndDate}
                                    min={stagedStartDate || undefined}
                                    max={todayStr}
                                    onChange={e => setStagedEndDate(e.target.value)}
                                />
                            </div>

                            {/* Multi-status — options depend on active tab */}
                            <div className="rv-filter-group">
                                <label className="rv-filter-label">Status</label>
                                <StatusMultiSelect
                                    value={stagedStatuses}
                                    onChange={setStagedStatuses}
                                    options={activeTabStatusOptions}
                                />
                            </div>

                            {/* Buttons */}
                            <div className="rv-filter-actions">
                                {/* FIXED: Disable Filter button when no valid staged filters are set */}
                                <button
                                    className="rv-apply-btn"
                                    onClick={handleApplyFilters}
                                    disabled={loading || !hasValidStagedFilters}
                                >
                                    Filter
                                </button>

                                {/* Show Clear whenever staged OR applied filters exist */}
                                {(hasActiveFilters || hasStagedFilters) && (
                                    <button className="rv-clear-filter-btn" onClick={handleClearFilters} title="Clear all filters">
                                        <X size={13} /> Clear
                                    </button>
                                )}
                            </div>

                            {/* ── Export ALL button (right side) ── */}
                            <div style={{ marginLeft: 'auto' }}>
                                <button
                                    className="rv-export-all-btn"
                                    onClick={handleExportAllToExcel}
                                    disabled={exportingAll || loading || currentCount === 0}
                                    title="Export all filtered data to Excel">
                                    {exportingAll
                                        ? <><span className="rv-spinner" /> Exporting...</>
                                        : <><FileSpreadsheet size={15} /> Export Excel</>}
                                </button>
                            </div>
                        </div>

                        {/* Active-filter chips */}
                        {hasActiveFilters && (
                            <div className="rv-active-filters">
                                <span className="rv-filter-chip-label">Active filters:</span>
                                {appliedStartDate && <span className="rv-filter-chip">From: {appliedStartDate}</span>}
                                {appliedEndDate && <span className="rv-filter-chip">To: {appliedEndDate}</span>}
                                {appliedStatuses.map(s => (
                                    <span key={s} className="rv-filter-chip">{getStatusDisplay(s)}</span>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* ── TABLE ──────────────────────────────────── */}
                    <div className="report-scrollable-container">
                        <div className="report-table-container">
                            <div className="report-table-wrapper">
                                <table className="report-table">
                                    <thead>
                                        <tr>
                                            <th>NO.</th>
                                            <th>TPL NO</th>
                                            <th>DATE CREATED</th>
                                            <th>PO ID</th>
                                            <th>FAB NO</th>
                                            <th>MODIFIED DATE</th>
                                            <th>{uploadModalProps['buildup']} STATUS</th>
                                            <th>{uploadModalProps['test']} STATUS</th>
                                            <th>{uploadModalProps['pdi']} STATUS</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentReports.length === 0 && !loading ? (
                                            <tr>
                                                <td colSpan={9} style={{ textAlign: 'center', padding: '48px 20px', color: '#999' }}>
                                                    {hasActiveFilters ? 'No records match the current filters.' : 'No records found.'}
                                                </td>
                                            </tr>
                                        ) : currentReports.map((report, idx) => (
                                            <tr key={report._id}>
                                                <td>{(currentPage - 1) * itemsPerPage + idx + 1}</td>
                                                <td>{report.tpl_no}</td>
                                                <td>{formatDateTime(report.cd)}</td>
                                                <td>{report.po_no}</td>
                                                <td>{report.fab_no}</td>
                                                <td>{formatDateTime(report.md)}</td>
                                                {['buildup', 'test', 'pdi'].map(type => (
                                                    <td key={type}>
                                                        <div className="report-status-cell">
                                                            {report[type]?.length > 0 ? (
                                                                <button className="report-view-btn"
                                                                    onClick={() => { addCustomPath(`${activeTab} - ${uploadModalProps[type]}`); handleViewSection(report, type); }}>
                                                                    <Eye size={18} />
                                                                </button>
                                                            ) : <div style={{ width: 32, height: 32 }} />}
                                                            <span className={getStatusClass(report[`${type}_status`])}>
                                                                {getStatusDisplay(report[`${type}_status`])}
                                                            </span>
                                                        </div>
                                                    </td>
                                                ))}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>

                    {/* ── PAGINATION ─────────────────────────────── */}
                    <div className="report-pagination">
                        <div className="report-rows-per-page">
                            <span>Rows per page:</span>
                            <select value={itemsPerPage} onChange={e => handlePageSizeChange(+e.target.value)}>
                                {[10, 25, 50, 100].map(v => <option key={v} value={v}>{v}</option>)}
                            </select>
                        </div>
                        {currentCount > 0 ? (
                            <span className="report-page-info">
                                Showing {(currentPage - 1) * itemsPerPage + 1}–{Math.min(currentPage * itemsPerPage, currentCount)} of {currentCount} entries
                            </span>
                        ) : <span>Showing 0–0 of 0 entries</span>}
                        <div className="report-pagination-controls">
                            <button onClick={handleFirstPage} disabled={currentPage === 1 || loading}>⟪</button>
                            <button onClick={handlePreviousPage} disabled={currentPage === 1 || loading}>⟨</button>
                            {currentCount > 0
                                ? <span className="report-page-numbers">Page {currentPage} of {mainTotalPages}</span>
                                : <span>Page 0 of 0</span>}
                            <button onClick={handleNextPage} disabled={currentPage >= mainTotalPages || loading}>⟩</button>
                            <button onClick={handleLastPage} disabled={currentPage >= mainTotalPages || loading}>⟫</button>
                        </div>
                    </div>
                </>
            )}

            <FullScreenImageViewer
                isOpen={fullScreenViewer.isOpen}
                onClose={handleCloseFullScreenViewer}
                images={fullScreenViewer.images}
                initialIndex={fullScreenViewer.initialIndex}
                viewType={fullScreenViewer.viewType}
            />
        </div>
    );
};

export default ReportView;